/**
 * @fileoverview ListMappingsPanel — inline table shown when a PlaceId group row
 * is expanded in the List tab.
 *
 * Renders all market venue mappings in the group with columns:
 * Market, Venue ID, Venue, Inserted On, Updated On, Updated By.
 */

import * as React from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from '@mui/material';
import type { MarketVenueMappingRecord } from '../utils';
import MarketLabel from './MarketLabel';

// ---------------------------------------------------------------------------
// Column width constants
// ---------------------------------------------------------------------------

/**
 * Percentage widths for the child mapping-row table columns.
 * The first column is a spacer aligning with the parent chevron.
 * Order: spacer, market, venueId, venue, insertedOn, updatedOn, updatedBy.
 */
const CHILD_COL_WIDTHS = {
    spacer:     '4%',
    market:     '14%',
    venueId:    '10%',
    venue:      '24%',
    insertedOn: '16%',
    updatedOn:  '16%',
    updatedBy:  '16%',
} as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Formats a UTC ISO string for display, or returns '—' when falsy.
 *
 * @param value - ISO date string or null/undefined.
 * @returns Short locale date+time string.
 */
function formatDate (value: string | null | undefined): string {
    if (!value) return '—';
    try {
        return new Date(value).toLocaleString();
    } catch {
        return value;
    }
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/** Props for {@link ListMappingsPanel}. */
export interface ListMappingsPanelProps {
    /** All records belonging to this PlaceId group. */
    records: MarketVenueMappingRecord[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Inline table shown when a PlaceId group row is expanded in the List tab.
 *
 * Renders all market venue mappings in the group with columns:
 * Market, Venue ID, Venue, Inserted On, Updated On, Updated By.
 *
 * @param props.records - All records belonging to this PlaceId group.
 */
const ListMappingsPanel: React.FC<ListMappingsPanelProps> = ({ records }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <Box
            sx={{
                py: 1,
                bgcolor: isDark ? 'grey.900' : 'grey.50',
                '& .MuiTableCell-root': {
                    borderColor: isDark ? 'grey.700' : undefined,
                },
            }}
        >
            <Table
                size="small"
                aria-label="market venue mappings in place-id group"
                sx={{ tableLayout: 'fixed', width: '100%' }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: CHILD_COL_WIDTHS.spacer, p: 0 }} />
                        <TableCell sx={{ fontWeight: 700, width: CHILD_COL_WIDTHS.market }}>Market</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: CHILD_COL_WIDTHS.venueId }}>Venue ID</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: CHILD_COL_WIDTHS.venue }}>Venue</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: CHILD_COL_WIDTHS.insertedOn }}>Inserted On</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: CHILD_COL_WIDTHS.updatedOn }}>Updated On</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: CHILD_COL_WIDTHS.updatedBy }}>Updated By</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {records.map(record => (
                        <TableRow key={record.id} hover>
                            <TableCell sx={{ p: 0 }} />

                            <TableCell>
                                <MarketLabel
                                    marketId={record.marketId}
                                    marketType={record.marketType}
                                />
                            </TableCell>

                            <TableCell>
                                {record.sourceVenueId ?? '—'}
                            </TableCell>

                            <TableCell>
                                <Typography variant="body2" component="span" display="block" noWrap>
                                    {record.marketVenueName || '—'}
                                </Typography>
                                {record.marketVenueCity && (
                                    <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                        {record.marketVenueCity}
                                    </Typography>
                                )}
                            </TableCell>

                            <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                {formatDate(record.insertedOn)}
                            </TableCell>

                            <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                {formatDate(record.updatedOn)}
                            </TableCell>

                            <TableCell sx={{ color: 'text.secondary' }}>
                                {record.updatedBy || '—'}
                            </TableCell>
                        </TableRow>
                    ))}
                    {records.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={7}>
                                <Typography variant="caption" color="text.secondary">
                                    No mappings in this group.
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Box>
    );
};

export default ListMappingsPanel;
